import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  styled,
  Chip,
  useTheme,
  useMediaQuery,
  Paper,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    padding: theme.spacing(2),
    maxWidth: '90%',
    margin: theme.spacing(2),
    background: theme.palette.background.paper,
    [theme.breakpoints.up('sm')]: {
      maxWidth: 450,
      padding: theme.spacing(3),
    },
  },
}));

const StatusHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const StatusIconWrapper = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.lighter || theme.palette.primary.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

const CompactInfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
  marginBottom: theme.spacing(2),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: 8,
  fontWeight: 500,
  backgroundColor: theme.palette.primary.lighter || theme.palette.primary.light,
  color: theme.palette.primary.dark,
}));

const PendingApprovalBanner = ({ userData, logout, onRefreshStatus }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      setShowConfirmDialog(false);
      
      const response = await axios.delete('/api/v1/accounts/user/pending-delete/');
      
      if (response.data.message === 'Account deleted successfully') {
        enqueueSnackbar('Account deleted successfully. You can now sign up again.', { 
          variant: 'success' 
        });
        
        // Clear any stored session data
        localStorage.clear();
        sessionStorage.clear();
        
        // Redirect to home page
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to delete account. Please try again.', 
        { variant: 'error' }
      );
      setLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      setRefreshing(true);
      
      if (onRefreshStatus) {
        // Call the provided refresh function
        await onRefreshStatus();
      } else {
        // Fallback: refresh the entire page
        window.location.reload();
      }
      
      enqueueSnackbar('Status updated', { variant: 'info' });
    } catch (error) {
      console.error('Error refreshing status:', error);
      enqueueSnackbar('Failed to refresh status. Please try again.', { 
        variant: 'error' 
      });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <StyledDialog 
        open 
        fullWidth 
        aria-labelledby="pending-approval-dialog-title" 
        maxWidth="sm"
        disableEscapeKeyDown={loading || refreshing}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' && (loading || refreshing)) {
            // eslint-disable-next-line no-useless-return
            return;
          }
        }}
      >
      <DialogContent sx={{ p: 0 }}>
        <StatusHeader>
          <StatusIconWrapper>
            <AccessTimeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </StatusIconWrapper>
          <Typography variant="h6" align="center" sx={{ fontWeight: 600, mb: 0.5 }}>
            Waiting for Approval
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Your admin will review your request soon
          </Typography>
        </StatusHeader>

        {/* Compact user info */}
        <CompactInfoCard elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {userData.companyName || 'Company'}
              </Typography>
            </Box>
            <StyledChip size="small" label="Pending" />
          </Box>
          
          {userData.userAllCampus && userData.userAllCampus.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {userData.userAllCampus[0].name}
              </Typography>
            </Box>
          )}
        </CompactInfoCard>

        <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mb: 2 }}>
          {userData.firstName} • {userData.email}
        </Typography>

        {/* Info about checking status */}
        <Box sx={{ 
          backgroundColor: theme.palette.info.lighter || theme.palette.info.light, 
          borderRadius: 2, 
          p: 1.5, 
          mb: 2,
          border: `1px solid ${theme.palette.info.main}`,
          opacity: 0.8
        }}>
          <Typography variant="caption" color="info.dark" align="center" display="block">
            Been approved? Tap refresh to check your status
          </Typography>
        </Box>

        {/* Info about mistakes */}
        <Box sx={{ 
          backgroundColor: theme.palette.grey[50], 
          borderRadius: 2, 
          p: 1.5, 
          mb: 2,
          border: `1px solid ${theme.palette.grey[200]}`
        }}>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            Wrong company or incorrect details?
            <br />
            Delete your account to start over
          </Typography>
        </Box>

        <ButtonContainer>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleRefreshStatus}
            disabled={loading || refreshing}
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            sx={{
              borderRadius: 2,
              py: 1.25,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme.shadows[2],
              },
            }}
          >
            {refreshing ? 'Checking Status...' : 'Refresh Status'}
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={() => setShowConfirmDialog(true)}
            disabled={loading || refreshing}
            sx={{
              borderRadius: 2,
              py: 1.25,
              textTransform: 'none',
            }}
          >
            Delete Account
          </Button>
          
          <Button
            fullWidth
            variant="text"
            color="inherit"
            onClick={logout}
            disabled={loading || refreshing}
            sx={{
              borderRadius: 2,
              py: 1.25,
              textTransform: 'none',
              color: 'text.secondary',
            }}
          >
            Sign Out
          </Button>
        </ButtonContainer>
      </DialogContent>
    </StyledDialog>

    {/* Confirmation Dialog */}
    <Dialog
      open={showConfirmDialog}
      onClose={() => !loading && setShowConfirmDialog(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteOutlineIcon color="error" />
          <Typography variant="h6">Confirm Delete Account</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete your account?
          You'll need to sign up again if you want to rejoin.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={() => setShowConfirmDialog(false)} 
          disabled={loading}
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeleteAccount}
          color="error"
          variant="contained"
          disabled={loading}
          size="small"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Deleting...' : 'Delete Account'}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default PendingApprovalBanner;